'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { BundleCard } from '../bundle-cards/bundle-card';
import { ClassicPdpUpsellItem } from '../classic-pdp-content';
import { UpsellProductCard } from './upsell-product-card';
import { UpsellServiceCard } from './upsell-service-card';

export interface UpsellCardsProps {
  items: ClassicPdpUpsellItem[];
  title?: string;
  subtitle?: string;
}

function renderUpsellCard(item: ClassicPdpUpsellItem) {
  if (item.kind === 'bundle') {
    return <BundleCard bundle={item.bundle} pdpPath={item.pdpPath} />;
  }

  if (item.kind === 'service') {
    return <UpsellServiceCard product={item.product} />;
  }

  return <UpsellProductCard product={item.product} />;
}

/**
 * Upsell section under the two-column grid: one card per item the editor
 * linked on the tile, bundles and single products side by side. Cards stack in
 * one column on small screens and form a grid from `sm` up. Title and
 * subtitle come from the same CMS chunk as the products, so the editor keeps
 * the wording next to the offers it introduces.
 */
export function UpsellCards({ items, title, subtitle }: Readonly<UpsellCardsProps>) {
  const t = useTranslations('ClassicProductDetail');

  if (items.length === 0) {
    return null;
  }

  // The editorial title names the section; the generic term only labels a
  // section the editor left without a heading.
  const sectionLabel = title ?? t('bundles');

  return (
    <section aria-label={sectionLabel} data-testid="classic-pdp-bundles" className="mt-12 lg:mt-16">
      {(title || subtitle) && (
        <div className="mb-6">
          {title && <h2 className="text-2xl font-bold text-neutral-100">{title}</h2>}
          {subtitle && <p className="mt-1 text-base text-neutral-55">{subtitle}</p>}
        </div>
      )}
      <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item) => (
          <li key={item.id}>{renderUpsellCard(item)}</li>
        ))}
      </ul>
    </section>
  );
}
